import { Controller } from "@hotwired/stimulus";
import * as bootstrap from "bootstrap";

export default class OffcanvasController extends Controller {
  static values = { scrollKey: String };

  connect() {
    this.initializeOffcanvas();
    if (this.hasScrollKeyValue) {
      this.saveSidebarScroll = () => this.saveScroll();
      this.restoreSidebarScroll = () => this.restoreScroll();
      this.element.addEventListener("scroll", this.saveSidebarScroll, true);
      this.element.addEventListener("click", this.saveSidebarScroll, true);
      this.element.addEventListener("shown.bs.offcanvas", this.restoreSidebarScroll);
      globalThis.addEventListener("pagehide", this.saveSidebarScroll);
      this.restoreFrame = requestAnimationFrame(this.restoreSidebarScroll);
    }

    document.addEventListener(
      "turbo:before-cache",
      this.handleBeforeCache
    );
    globalThis.addEventListener("pagehide", this.handleBeforeCache);
  }

  disconnect() {
    this.saveScroll();
    cancelAnimationFrame(this.restoreFrame);
    this.element.removeEventListener("scroll", this.saveSidebarScroll, true);
    this.element.removeEventListener("click", this.saveSidebarScroll, true);
    this.element.removeEventListener("shown.bs.offcanvas", this.restoreSidebarScroll);
    globalThis.removeEventListener("pagehide", this.saveSidebarScroll);
    document.removeEventListener(
      "turbo:before-cache",
      this.handleBeforeCache
    );
    globalThis.removeEventListener("pagehide", this.handleBeforeCache);

    this.disposeOffcanvas();
  }

  initializeOffcanvas() {
    this.offcanvas = bootstrap.Offcanvas.getOrCreateInstance(
      this.element
    );
  }

  open() {
    this.element.classList.remove("d-none");

    this.initializeOffcanvas();
    this.offcanvas.show();
  }

  close() {
    this.offcanvas?.hide();
  }

  handleBeforeCache = () => {
    this.saveScroll();
    this.disposeOffcanvas();
  };

  scrollElements() {
    return [this.element, this.element.querySelector(".offcanvas-body")].filter(Boolean);
  }

  saveScroll() {
    if (!this.hasScrollKeyValue || this.restoringScroll) return;
    try {
      const key = `sidebar-scroll:${this.scrollKeyValue}`;
      const previous = JSON.parse(sessionStorage.getItem(key) || "[]");
      const positions = this.scrollElements().map((element, index) =>
        element.clientHeight > 0 ? element.scrollTop : (previous[index] || 0));
      sessionStorage.setItem(key, JSON.stringify(positions));
    } catch { /* Navigation must still work if storage is unavailable. */ }
  }

  restoreScroll() {
    if (!this.hasScrollKeyValue) return;
    try {
      const positions = JSON.parse(sessionStorage.getItem(`sidebar-scroll:${this.scrollKeyValue}`) || "[]");
      this.restoringScroll = true;
      this.scrollElements().forEach((element, index) => {
        if (!Number.isFinite(positions[index]) || element.clientHeight === 0) return;
        const behavior = element.style.scrollBehavior;
        element.style.scrollBehavior = "auto";
        element.scrollTop = positions[index];
        element.style.scrollBehavior = behavior;
      });
    } catch { /* Keep the default position if stored state is unavailable. */ }
    finally { this.restoringScroll = false; }
  }

  disposeOffcanvas() {
    this.offcanvas = bootstrap.Offcanvas.getInstance(this.element) || this.offcanvas;
    if (this.offcanvas) {
      // Finish pending animations before Bootstrap clears its element.
      this.element.dispatchEvent(new Event("transitionend"));
      this.offcanvas.hide();
      this.element.dispatchEvent(new Event("transitionend"));
      this.offcanvas.dispose();
      this.offcanvas = null;
    }

    for (const backdrop of document
      .querySelectorAll(".offcanvas-backdrop")) backdrop.remove();

    document.body.classList.remove("offcanvas-backdrop");
    document.body.style.removeProperty("overflow");
    document.body.style.removeProperty("padding-right");

    this.element.classList.remove("show", "showing", "hiding");
    // Bootstrap does not clear aria-hidden when reopening the sidebar.
    this.element.removeAttribute("aria-hidden");
    this.element.removeAttribute("aria-modal");
    this.element.removeAttribute("role");
  }
}

